import { ArrowDownTrayIcon, ClockIcon, LockClosedIcon, PaperClipIcon } from '@heroicons/react/24/solid';
import React, { useEffect, useState } from 'react'
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import FilePreview from '@/components/FilePreview';
import { closeDiscussion, getRfqMessages, resetStatus, saveRfqDiscussion } from '@/store/slices/rfqSlice';
import Toaster from '@/components/Toaster';
import { useSelector, useDispatch } from "react-redux";
import DocumentViewModal from '@/components/DocumentViewModal';
import moment from "moment";
import useHasPermission from '@/components/useHasPermission';
import Swal from 'sweetalert2';
import DownloadFile, { handleDownloadURL } from '@/components/DownloadFile';
import { useTranslation } from 'react-i18next';


const chatSchema = yup.object().shape({
  rfq_id: yup.string().required("RFQ ID is required"),

  message: yup
    .string()
    .max(300, "message_max_length")
    .nullable(),

  file: yup
    .array()
    .nullable()
    .test("fileType", "file_type_images_only", function (files) {
      if (!files || files.length === 0) return true;

      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
        "image/gif"
      ];

      return files.every(file => allowedTypes.includes(file.type));
    })
    .test("fileSize", "file_size_too_large", function (files) {
      if (!files || files.length === 0) return true;

      return files.every(file => file.size <= 20 * 1024 * 1024);
    }),
})
  .test(
    "messageOrFile",
    "message_or_file_required",
    function (values) {
      const { message, file } = values;

      const hasMessage = message && message.trim() !== "";
      const hasFile = file && Array.isArray(file) && file.length > 0;

      return hasMessage || hasFile;
    }
  );

const primaryYellow = 'bg-[#F4B11F]';

export function RfqChat({ rfq_id, customer_id, purchase_order = '' }) {
  const dispatch = useDispatch();
  const [isViewFileModalOpen, setIsViewFileModalOpen] = useState(false);
  const [documentUrl, setDocumentUrl] = useState('');
  const { t } = useTranslation('rfq');
  const { t: ct } = useTranslation("common");

  const hasUpdatePermission = useHasPermission(`rfq.update`);

  const { rfq_discussion_messages,list_loading, loading } = useSelector((s) => s.rfq);
  const { user } = useSelector((s) => s.user);

  const firstChatDate =
    rfq_discussion_messages?.length > 0
      ? rfq_discussion_messages[0].message_time.split(",")[0]
      : null;

  useEffect(() => {
    if (!rfq_id) return;

    // Initial call (always)
    dispatch(getRfqMessages(rfq_id));
    const interval = setInterval(() => {
      const isChatOpen =
        rfq_discussion_messages?.[0]?.customer_chat_status === "open";

      if (document.visibilityState === "visible" && isChatOpen) {
        dispatch(getRfqMessages(rfq_id));
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [rfq_id, rfq_discussion_messages?.[0]?.customer_chat_status]);

  const initialForm = {
    rfq_id: rfq_id,
    message: null,
    file: []
  };

  // ✅ CHANGE 1: added getValues
  const { register, handleSubmit, setValue, watch, reset, getValues } = useForm({
    resolver: yupResolver(chatSchema, {
      // Translate yup error messages
      // eslint-disable-next-line no-template-curly-in-string
      yup_mixed_required: '${path} is required'
    }),
    defaultValues: initialForm
  });

  const onSubmit = async (data) => {
    if (customer_id === null) {
      Toaster.error(t("create_customer_for_rfq_error"));
      return;
    }
    try {
      const result = await dispatch(saveRfqDiscussion(data)).unwrap();
      if (result.status) {
        reset(initialForm);
        dispatch(getRfqMessages(rfq_id));
      }
    } catch (err) {
      dispatch(resetStatus());
    }
  };

  const attachFiles = watch('file');

  // ✅ CHANGE 2: ref for text input to auto-focus after file select
  const textInputRef = React.useRef(null);

  const getInitials = (name = "") => {
    return name
      .trim()
      .split(/\s+/)
      .map(word => word[0].toUpperCase())
      .join("");
  };

  const messagesContainerRef = React.useRef(null);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [rfq_discussion_messages]);

  const formatMessageTime = (messageTime) => {
    const msgMoment = moment(messageTime);
    const cutoffTime = moment().subtract(1, 'day');

    if (msgMoment.isAfter(cutoffTime)) {
      return msgMoment.fromNow();
    } else {
      return msgMoment.calendar(null, {
        sameDay: '[Today]',
        nextDay: '[Tomorrow]',
        nextWeek: 'dddd',
        lastDay: '[Yesterday]',
        lastWeek: '[Last] dddd',
        sameElse: 'DD MMM YYYY'
      });
    }
  };

  const closeChat = (type) => {
    let title = t('confirm_close_discussion_title');
    let text = t('confirm_close_discussion_text');
    if (type === 'open') {
      title = t('confirm_open_discussion_title');
      text = t('confirm_open_discussion_text');
    }
    Swal.fire({
      title: title,
      text: text,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: t('confirm_discussion_action_button', { type: type }),
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(closeDiscussion({ rfq_id: rfq_id, status: type }))
          .unwrap()
          .then((res) => {
            if (res?.status) {
              Toaster.success(t('discussion_status_success', { status: type }));
              dispatch(getRfqMessages(rfq_id));
            } else {
              Toaster.error(res?.message || ct("somethingWentWrong"));
            }
          })
          .catch((error) => {
            console.error("Failed:", error);
            Toaster.error(ct("actionFailed"));
          });
      }
    });
  };

  const isValidUrl = (text) => {
    try {
      new URL(text);
      return true;
    } catch (_) {
      return false;
    }
  };

  return (
    <div className='px-2 md:px-6'>
      <div className="flex flex-wrap gap-3 mb-8">
        {rfq_discussion_messages
          ?.filter(msg => msg.quotations && msg.type === 'quotation' && msg.quotations.length > 0)
          ?.map((msg, index) => {
            const quotation = msg.quotations[0];
            const status = msg?.status;

            const statusClasses = {
              approved: "bg-[#16CB0926] text-[#01B735] border-[#16CB0926]",
              pending: "bg-yellow-50 text-[#f9a825] border-yellow-50",
              rejected: "bg-[#CB090926] text-[#CB0909] border-[#CB090926]",
            };

            const appliedClasses =
              statusClasses[status] ?? "bg-gray-100 text-blue-800 border-gray-300";

            return (
              <div className="flex flex-col items-start gap-1">
                <button
                  key={index}
                  onClick={() => {
                    setIsViewFileModalOpen(true);
                    setDocumentUrl(quotation.file);
                  }}
                  className={`
                  flex items-center gap-2
                  px-4 py-2
                  text-sm font-semibold
                  rounded-lg
                  shadow-sm
                  hover:bg-blue-100
                  hover:border-blue-300
                  hover:shadow-md
                  transition-all duration-200
                  ${appliedClasses}
                `}
                >
                  📄 {quotation.file_name}
                </button>

                {msg.user?.id && (
                  <p className="text-xs text-gray-500 ml-1">
                    {msg.user?.type}
                    <span className="text-gray-400">
                      {" "}({msg.user?.designation})
                    </span>
                  </p>
                )}
              </div>
            );
          })}
        {purchase_order && <div className="flex flex-col items-start gap-1">
          <button
            className={`
              flex items-center gap-2
              px-4 py-2
              text-sm font-semibold
              rounded-lg
              shadow-sm
              hover:bg-blue-100
              hover:border-blue-300
              hover:shadow-md
              transition-all duration-200
              bg-[#16CB0926] text-[#01B735] border-[#16CB0926]
            `}
            onClick={() => handleDownloadURL({fileUrl:purchase_order?.url})}
          >
            <ArrowDownTrayIcon className='w-4 h-4 text-blue-400'/>  {purchase_order?.file_name}
          </button>

          <p className="text-xs text-gray-500 ml-1">
            {t('purchase_order_issued_by_customer')}
          </p>
        </div>}
      </div>

      {rfq_discussion_messages?.length > 0 && rfq_discussion_messages[0].customer_chat_status === 'closed' && (
        <div className="flex items-center justify-between gap-4 rounded-md border border-red-300 bg-red-50 px-4 py-3 mb-2">
          <div className="flex items-start gap-3">
            <LockClosedIcon className="h-5 w-5 text-red-500 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-600">
                {t('discussion_closed_for_customer')}
              </p>
              <p className="text-xs text-red-500">
                {t('tap_to_open_discussion')}
              </p>
            </div>
          </div>
          {['Administrator', 'Sub Admin', 'Sales Representative'].includes(user?.roles?.[0]) && (
            <button
              onClick={() => closeChat('open')}
              className="rounded-full bg-red-500 px-4 py-1.5 text-xs font-semibold text-white hover:bg-red-600 transition"
            >
              {' '}
              {t('open_button')}{' '}
            </button>
          )}
        </div>
      )}

      {rfq_discussion_messages?.length > 0 && rfq_discussion_messages[0].customer_chat_status === "open" && (
        <div className="mb-2 flex items-center justify-between gap-4 rounded-md border border-green-300 bg-green-50 px-4 py-3">
          <div className="flex items-start gap-3">
            <LockClosedIcon className="mt-0.5 h-5 w-5 text-green-600" />
            <div>
              <p className="text-sm font-semibold text-green-700">
                {t('discussion_open_for_customer')}
              </p>
              <p className="text-xs text-green-600">
                {t('tap_to_close_discussion')}
              </p>
            </div>
          </div>
          {['Administrator', 'Sub Admin', 'Sales Representative'].includes(user?.roles?.[0]) && (
            <button
              className="rounded-full bg-green-600 px-4 py-1.5 text-xs font-semibold text-white transition hover:bg-green-700"
              onClick={() => closeChat('closed')}
            >
              {t('close_button')}
            </button>
          )}
        </div>
      )}

      <div className="bg-[#F4F4F4] border rounded-lg p-1 md:p-4">
        <div className="relative mt-6">

          {(!list_loading && rfq_discussion_messages?.length === 0) && (
            <div className="flex items-center justify-center py-6">
              <p className="text-gray-600 text-sm leading-6">{t('no_messages_yet')}</p>
            </div>
          )}

          {rfq_discussion_messages?.length !== 0 && <>
            {/* Vertical Line */}
            <div className="absolute left-5 top-0 bottom-20 w-0.5 bg-green-500"></div>

            {/* Messages */}
            <div ref={messagesContainerRef} className="space-y-8 max-h-[65vh] overflow-y-auto pr-3 custom-scrollbar">
              {(() => {
                let lastDate = null;
                return (rfq_discussion_messages?.map((msg) => {
                  const currentDateString = moment(msg.message_time).format("DD MMM YYYY");
                  const showDateSeparator = currentDateString !== lastDate;
                  lastDate = currentDateString;

                  return (<React.Fragment key={msg.id}>
                    {showDateSeparator && (
                      <div className="flex justify-start my-4 relative z-20">
                        <div className="text-sm bg-[#CCBDFF] inline-block px-3 py-1 rounded-md font-medium text-[#1601B7] shadow-sm">
                          {moment(msg.message_time).calendar(null, {
                            sameDay: '[Today]',
                            nextDay: '[Tomorrow]',
                            nextWeek: 'dddd',
                            lastDay: '[Yesterday]',
                            lastWeek: '[Last] dddd',
                            sameElse: 'DD MMM YYYY'
                          })}
                        </div>
                      </div>
                    )}
                    <div key={msg.id} className="relative flex gap-4">
                      {/* Icon */}
                      <div className="relative z-10">
                        {msg?.user?.full_name && <div
                          className={`w-10 h-10 flex justify-center items-center text-white font-bold rounded-full ${user.id === msg?.user?.id ? 'bg-green-600' : 'bg-pink-600'}`}
                        >
                          {getInitials(msg.user.full_name)}
                        </div>}
                      </div>

                      {/* Card */}
                      <div className="p-4 flex-1 border-b border-gray-300 last:border-b last:border-gray-300">
                        <span className="absolute top-5 right-4 text-right text-xs text-gray-400 leading-4 flex items-center gap-1">
                          <ClockIcon className="w-4 h-4" />
                          {moment(msg.message_time).format("DD MMM, YYYY hh:mm A")}
                        </span>

                        {msg.user?.id && <p className="font-semibold text-gray-800 mb-1">{msg.user?.full_name}</p>}
                        {msg.user?.id && <p className="font-small text-gray-400 mb-1">{msg.user?.type}({msg.user?.designation})</p>}

                        <p className="text-gray-600 text-sm leading-6 mb-2">
                          {isValidUrl(msg.message) ? (
                            <a
                              href={msg.message}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 underline"
                            >
                              {msg.message}
                            </a>
                          ) : (
                            msg.message
                          )}
                        </p>

                        {/* {msg.quotations && msg.quotations.length > 0 && (
                          <div className="flex items-start gap-3 flex-wrap mt-2">
                            <div className="flex flex-col gap-1">
                              {msg.quotations.map((fileItem, index) => (
                                <a
                                  key={index}
                                  onClick={() => {
                                    setIsViewFileModalOpen(true);
                                    setDocumentUrl(fileItem.file);
                                  }}
                                  className="text-blue-600 text-sm underline cursor-pointer hover:text-blue-800"
                                >
                                  {fileItem.file_name}
                                </a>
                              ))}
                            </div>

                            {msg?.status && msg?.type === 'quotation' && (
                              <span
                                className={`${msg.status === 'pending'
                                  ? 'text-orange-500'
                                  : msg.status === 'approved'
                                    ? 'text-green-500'
                                    : 'text-red-500'
                                  } text-sm font-medium`}
                              >
                                {msg.status === 'pending' ? 'Approval Pending' : msg._status}
                              </span>
                            )}
                          </div>
                        )} */}
                        {msg.quotations && msg.quotations.length > 0 && (
                          <div className="mt-2 flex flex-col gap-1 border-gray-100">
                            {/* File Row Container: File List on left, Status Badge/Text on right */}
                            <div className="flex flex-wrap items-start gap-3">
                              <div className="flex flex-col gap-1">
                                {msg.quotations.map((fileItem, index) => (
                                  <a
                                    key={index}
                                    onClick={() => {
                                      setIsViewFileModalOpen(true);
                                      setDocumentUrl(fileItem.file);
                                    }}
                                    className="cursor-pointer text-sm text-blue-600 underline hover:text-blue-800"
                                  >
                                    {fileItem.file_name}
                                  </a>
                                ))}
                              </div>

                              {msg?.status && msg?.type === 'quotation' && (
                                <span
                                  className={`${
                                    msg.status === 'pending'
                                      ? 'text-orange-500'
                                      : msg.status === 'approved'
                                        ? 'text-green-500'
                                        : 'text-red-500'
                                  } text-sm font-medium`}
                                >
                                  {msg.status === 'pending' ? t('approval_pending_status') : msg._status}
                                </span>
                              )}
                            </div>

                            {/* Single Line Rejection Reason Inline Block */}
                            {msg.status === 'rejected' && msg.reason && (
                              <div className="text-xs text-red-600 font-medium flex items-start gap-1 mt-0.5">
                                <span className="font-semibold whitespace-nowrap">{t('rejection_reason_label')}</span>
                                <span className="text-gray-700 break-all">{msg.reason}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </React.Fragment>)
                }));
              })()}
            </div>
          </>}

          {list_loading && (
            <div className="flex items-center justify-center py-2">
              <span className="flex space-x-1">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-bounce"></span>
                <span className="w-2 h-2 bg-green-500 rounded-full animate-bounce [animation-delay:0.15s]"></span>
                <span className="w-2 h-2 bg-green-500 rounded-full animate-bounce [animation-delay:0.3s]"></span>
              </span>
            </div>
          )}

          {/* File preview */}
          {!loading && <div className="relative w-3/4 mx-auto mt-4">
            {attachFiles?.length > 0 && (
              <div className="-top-32 left-0 right-0 z-20 px-1">
                <div className="flex gap-3 rounded-xl border border-gray-200 bg-black/10">
                  {attachFiles.map((file, index) => (
                    <div key={index}>
                      <FilePreview
                        key={index}
                        file={file}
                        onRemove={() => {
                          const updated = attachFiles.filter((_, i) => i !== index);
                          setValue("file", updated);
                        }}
                        isView={false}
                        containerClass="relative w-16 h-16 rounded-md overflow-hidden border border-gray-400"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>}

          {/* Comment Box */}
          {hasUpdatePermission &&
            <div className="border bg-green-900 p-4 rounded-lg">
              <div className="flex items-center gap-1 md:gap-3 bg-white rounded-lg px-1 md:px-3 relative">

                {/* Attach File Button */}
                <label htmlFor="chatFile" className='cursor-pointer'>
                  <input
                    id="chatFile"
                    type="file"
                    className="hidden"
                    {...register("file")}
                    onChange={(e) => {
                      const selected = Array.from(e.target.files || []);

                      const allowedTypes = [
                        "application/pdf",
                        "image/jpg",
                        "image/jpeg",
                        "image/png",
                        "image/webp",
                      ];
                      const validFiles = selected.filter((file) =>
                        allowedTypes.includes(file.type)
                      );
                      if (validFiles?.length !== selected?.length) {
                        Toaster.error(t("unsupported_file_type_images"));
                      }
                      if (validFiles?.length > 0) {
                        setValue("file", validFiles);
                        setTimeout(() => textInputRef.current?.focus(), 0);
                      }
                    }}
                  />
                  <PaperClipIcon className='h-6 w-6 m-2'></PaperClipIcon>
                </label>

                {/* Input */}
                <input
                  type="text"
                  placeholder={t('write_comment_placeholder')}
                  className="flex-1 p-3 outline-none text-sm bg-transparent"
                  {...register('message')}
                  ref={(e) => {
                    register('message').ref(e);
                    textInputRef.current = e;
                  }}
                  disabled={loading}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const currentFile = getValues('file');
                      const currentMessage = getValues('message');
                      const hasFile = currentFile && currentFile.length > 0;
                      const hasMessage = currentMessage && currentMessage.trim() !== '';
                      if (hasFile || hasMessage) {
                        handleSubmit(onSubmit)();
                      }
                    }
                  }}
                />

                {/* Send Button */}
                <button
                  disabled={loading}
                  onClick={handleSubmit(onSubmit)}
                  className={`ml-3 absolute right-1 w-10 h-10 flex items-center justify-center rounded-full ${primaryYellow} hover:bg-green-700 transition`}
                >
                  <svg className="w-5 h-5 text-white transform rotate-45" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
                  </svg>
                </button>
              </div>
            </div>
          }
        </div>
        {isViewFileModalOpen && (
          <DocumentViewModal
            documentUrl={documentUrl}
            setIsDocumentViewModalOpen={setIsViewFileModalOpen}
          />
        )}
      </div>
    </div>
  )
}

export default RfqChat;